import { useRef, useEffect, useCallback, useMemo } from 'react';
import * as Sentry from '@sentry/browser';
import config from '@sketch/env-config';
import { domains } from '@sketch/constants';

// Helpers
const GA_PROPERTY = config.google_analytics;
// Creates a new iframe to inject GA script otherwise returns the already created iframe
const getOrCreateIframe = () => {
    var _a, _b, _c;
    const gaIframe = document.getElementById('ga-iframe');
    if (gaIframe) {
        return gaIframe;
    }
    // Create an iframe to include the GA script
    const iframe = document.createElement('iframe');
    iframe.setAttribute('id', 'ga-iframe');
    iframe.setAttribute('hidden', 'true');
    // Add the GA script to the iframe
    const html = `
  <head>
    <script src="https://www.googletagmanager.com/gtag/js?id=${GA_PROPERTY}"></script>
  </head>
  `;
    document.body.appendChild(iframe);
    (_a = iframe.contentWindow) === null || _a === void 0 ? void 0 : _a.document.write(html);
    // Close the document to prevent parent document to keep loading
    (_b = iframe.contentWindow) === null || _b === void 0 ? void 0 : _b.document.close();
    // Init GA
    iframe.contentWindow.dataLayer = ((_c = iframe.contentWindow) === null || _c === void 0 ? void 0 : _c.dataLayer) || [];
    function gtag(...args) {
        var _a, _b;
        // eslint-disable-next-line prefer-rest-params
        (_b = (_a = iframe.contentWindow) === null || _a === void 0 ? void 0 : _a.dataLayer) === null || _b === void 0 ? void 0 : _b.push(arguments);
    }
    gtag('js', new Date());
    gtag('config', GA_PROPERTY, {
        send_page_view: false,
        // Anonymize IP for user privacy
        anonymize_ip: true,
        // Use Navigator.sendBeacon if supported
        transport_type: 'beacon',
    });
    // Set the user-scoped custom dimension to Anonymous
    gtag('set', 'user_properties', {
        authentication: 'Anonymous',
    });
    return iframe;
};
// Create a function that makes sure Sentry is only
// triggered once if GA property doesn't exist and
// trackEvent is called multiple times
const createSentryUniqueWarning = () => {
    let messageSent = false;
    return () => {
        if (messageSent)
            return;
        Sentry.withScope(() => {
            Sentry.captureMessage('Google Analytics ID was not found');
        });
        messageSent = true;
    };
};
const uniqueSentryWarning = createSentryUniqueWarning();
const resetCookie = (cookie) => {
    const cookieName = cookie.split('=')[0].trim();
    // Remove all cookies for all listed domains
    domains.forEach(domain => {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}`;
    });
};
const removeGACookies = () => {
    // Remove iframe
    const gaIframe = document.getElementById('ga-iframe');
    if (gaIframe) {
        document.body.removeChild(gaIframe);
    }
    // Remove all cookies
    document.cookie
        .split(';')
        .filter(cookie => cookie.trim().startsWith('_g'))
        .forEach(resetCookie);
};
/**
 * useGoogleAnalytics
 *
 * We are very strict of what we track and how long the
 * GA cookie remains. We only track anonymous pages (Sign in and Sign up).
 *
 * For that reason, this hook creates and iframe with GA script
 * and tracks a pageview. As soon as the unmount ocurrs we remove the iframe and cookies
 *
 * This limits GA presence on purpose, for now.
 * */
const useGoogleAnalytics = (props) => {
    const isSignedInRef = useRef(props.isSignedIn);
    isSignedInRef.current = props.isSignedIn;
    useEffect(() => {
        // Because we ONLY want GA in anonymous pages (Sign in, Sign up)
        // we remove the iframe and all the GA cookies on unmount
        return () => {
            removeGACookies();
        };
    }, []);
    const trackGAEvent = useCallback(function trackGAEvent(..._args) {
        var _a, _b;
        const isSignedIn = isSignedInRef.current;
        // We really don't want Google Analytics anywhere near non-anonymous pages.
        if (isSignedIn) {
            removeGACookies();
            return;
        }
        // The iframe was causing Cypress to timeout in the visit command, even if the iframe content
        // was an empty string. Since we don't want this to run on cypress we are returning null here.
        if (window === null || window === void 0 ? void 0 : window.Cypress) {
            return;
        }
        if (!GA_PROPERTY) {
            uniqueSentryWarning();
            return;
        }
        const iframe = getOrCreateIframe();
        // Push event to the dataLayer
        // We are using the "arguments" to forward all gtag() arguments to the dataLayer
        // eslint-disable-next-line prefer-rest-params
        (_b = (_a = iframe.contentWindow) === null || _a === void 0 ? void 0 : _a.dataLayer) === null || _b === void 0 ? void 0 : _b.push(arguments);
    }, []);
    return useMemo(() => ({ trackGAEvent, removeGACookies }), 
    // Let's make sure that returned reference does not change with every rerender
    // and we know that trackGAEvent will never change as its deps list is just empty array
    // so to please eslint - let's just add this dependency here, but we sill know that it will never change
    [trackGAEvent]);
};

export { useGoogleAnalytics };
